import React from 'react';
import { TFunction } from 'react-i18next';
import { CustomSelect, MUIDataTableColumnDef, MUIDataTableMeta, Typography } from 'web-library';
import {
  MUIDataTableColumn,
  TableActionTypes,
  TableState,
} from 'web-library/stories/organism/DataTable/DataTable';

import { customFilterChipRender } from '../../../hooks/table';
import { SelectChangeEvent } from '@mui/material';

export const transactionTypeColumn = (
  classes: Record<string,string>,
  state: TableState,
  tableDispatch: React.Dispatch<TableActionTypes>,
  t: TFunction,
  filterCodeTransactionType: any,
  filterMappingTransactionType: any
): MUIDataTableColumnDef => ({
  name: 'transactionType',
  label: t('admin->>PaymentReport.TransactionType'),
  options: {
    sort: false,
    filter: true,
    filterType: 'custom',
    customFilterListOptions: {
      render: (v:any) => [filterMappingTransactionType[v[0]].children],
      update: (filterList: string[][],
              filterPos: number,
              index: number)  => {
        // update table filter state
        tableDispatch({
          type: 'UPDATE_FILTERS',
          payload: { ...state.filters, transactionType: [] },
        });

        // update table inner filter
        return customFilterChipRender(filterList, filterPos, index);
      },
    },
    filterOptions: {
      fullWidth: true,
      display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
        <CustomSelect
          className={classes.select}
          // eslint-disable-next-line react/destructuring-assignment
          value={filterList[index][0]}
          displayEmpty
          menuItems={filterCodeTransactionType}
          onChange={(event) => {
            // eslint-disable-next-line no-param-reassign
            filterList[index][0] = event.target.value as string;
            onChange(filterList[index], index, column);
          }}
          label={t('admin->>PaymentReport.TransactionType')}
          labelId="inputSelectLabel-transactionType"
          id="inputSelect-transactionType"
        />
      ),
    },
  },
});

const filterOnChange = (
  filterList: string[][],
  onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void,
  index: number,
  column: MUIDataTableColumn,
  event: SelectChangeEvent<unknown>
) => {
  // eslint-disable-next-line no-param-reassign
  filterList[index] = event.target.value as string[];
  onChange(filterList[index], index, column);
  window.dispatchEvent(new CustomEvent('resize'));
};

export const examLevelColumn = (
  classes: Record<string,string>,
  state: TableState,
  tableDispatch: React.Dispatch<TableActionTypes>,
  t: TFunction,
  filterCodeExamLevel: any,
  filterMappingExamLevel: any
): MUIDataTableColumnDef => ({
  name: 'examLevel',
  label: t('admin->>PaymentReport.ExamLevel'),
  options: {
    sort: false,
    filter: true,
    filterType: 'custom',
    customFilterListOptions: {
      render: (v:any) => {
        if (v.length > 1) {
          return [`${filterMappingExamLevel[v[0]].children}, ..`];
        }
        if (v.length === 1) {
          return [filterMappingExamLevel[v[0]].children];
        }
        return [];
      },
      update: (filterList: string[][],
filterPos: number,
index: number) => {
        // update table filter state
        tableDispatch({
          type: 'UPDATE_FILTERS',
          payload: { ...state.filters, examLevel: [] },
        });

        // update table inner filter
        return customFilterChipRender(filterList, filterPos, index);
      },
    },
    filterOptions: {
      fullWidth: true,
      display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
        <CustomSelect
          className={classes.select}
          displayEmpty
          variant="filled"
          multiple
          // eslint-disable-next-line no-param-reassign,react/destructuring-assignment
          value={filterList[index]}
          menuItems={filterCodeExamLevel}
         onChange={(e: SelectChangeEvent<unknown>) => {
            filterOnChange(filterList, onChange, index, column, e);
          }}
          label={t('admin->>PaymentReport.ExamLevel')}
          labelId="inputSelectLabel-examLevel"
          id="inputSelect-examLevel"
          renderValue={(selected: unknown | string[]) =>
            Array.isArray(selected) && selected.length > 0
              ? // @ts-ignore
                filterCodeExamLevel
                  .filter((examLevel: any) => selected.includes(examLevel.value))
                  .map((s: any) => s.children)
                  .join(', ')
              : t('admin->>PaymentReport.SelectExamLevel')
          }
        />
      ),
    },
  },
});

export const privateCenterColumn = (
  classes: Record<string,string>,
  state: TableState,
  tableDispatch: React.Dispatch<TableActionTypes>,
  t: TFunction,
  filterCodePrivateCenter: any,
  filterMappingPrivateCenter: any
): MUIDataTableColumnDef => ({
  name: 'organisationName',
  label: t('admin->>PaymentReport.PrivateCentre'),
  options: {
    sort: true,
    filter: true,
    filterType: 'custom',
    customBodyRender: (value: any, tableMeta: MUIDataTableMeta, updateValue: (value: any) => void)  => (
      <Typography variant="body2">{value || 'N/A'}</Typography>
    ),
    customFilterListOptions: {
      render: (v:any) => [filterMappingPrivateCenter[v[0]].children],
      update: (filterList: string[][],
filterPos: number,
index: number) => {
        // update table filter state
        tableDispatch({
          type: 'UPDATE_FILTERS',
          payload: { ...state.filters, organisationName: [] },
        });

        // update table inner filter
        return customFilterChipRender(filterList, filterPos, index);
      },
    },
    filterOptions: {
      fullWidth: true,
      display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
        <CustomSelect
          className={classes.select}
          // eslint-disable-next-line react/destructuring-assignment
          value={filterList[index][0]}
          displayEmpty
          menuItems={filterCodePrivateCenter}
          onChange={(event) => {
            // eslint-disable-next-line no-param-reassign
            filterList[index][0] = event.target.value as string;
            onChange(filterList[index], index, column);
          }}
          label={t('admin->>PaymentReport.PrivateCentre')}
          labelId="inputSelectLabel-privateCentre"
          id="inputSelect-privateCentre"
        />
      ),
    },
  },
});

export const paymentMethodColumn = (
  classes: Record<string,string>,
  state: TableState,
  tableDispatch: React.Dispatch<TableActionTypes>,
  t: TFunction,
  filterCodePaymentMethod: any,
  filterMappingPaymentMethod: any
): MUIDataTableColumnDef => ({
  name: 'paymentMethod',
  label: t('admin->>PaymentReport.PaymentMethod'),
  options: {
    sort: false,
    filter: true,
    filterType: 'custom',
    customFilterListOptions: {
      render: (v:any) => {
        if (v.length > 1) {
          return [`${filterMappingPaymentMethod[v[0]].children}, ..`];
        }
        if (v.length === 1) {
          return [filterMappingPaymentMethod[v[0]].children];
        }
        return [];
      },
      update: (filterList: string[][],
              filterPos: number,
              index: number) => {
        // update table filter state
        tableDispatch({
          type: 'UPDATE_FILTERS',
          payload: { ...state.filters, paymentMethod: [] },
        });

        // update table inner filter
        return customFilterChipRender(filterList, filterPos, index);
      },
    },
    filterOptions: {
      fullWidth: true,
      display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
        <CustomSelect
          className={classes.select}
          displayEmpty
          variant="filled"
          multiple
          // eslint-disable-next-line no-param-reassign,react/destructuring-assignment
          value={filterList[index]}
          menuItems={filterCodePaymentMethod}
         onChange={(e: SelectChangeEvent<unknown>) => {
            filterOnChange(filterList, onChange, index, column, e);
          }}
          label={t('admin->>PaymentReport.PaymentMethod')}
          labelId="inputSelectLabel-paymentMethod"
          id="inputSelect-paymentMethod"
          renderValue={(selected: unknown | string[]) =>
            Array.isArray(selected) && selected.length > 0
              ? // @ts-ignore
                filterCodePaymentMethod
                  .filter((paymentMethod: any) => selected.includes(paymentMethod.value))
                  .map((s: any) => s.children)
                  .join(', ')
              : t('admin->>PaymentReport.SelectPaymentMethod')
          }
        />
      ),
    },
  },
});

export const paymentStatusColumn = (
  classes: Record<string,string>,
  state: TableState,
  tableDispatch: React.Dispatch<TableActionTypes>,
  t: TFunction,
  filterCodePaymentStatus: any,
  filterMappingPaymentStatus: any
): MUIDataTableColumnDef => ({
  name: 'paymentStatus',
  label: t('admin->>PaymentReport.PaymentStatus'),
  options: {
    sort: false,
    filter: true,
    filterType: 'custom',
    display: true,
    customFilterListOptions: {
      render: (v:any) => {
        if (v.length > 1) {
          return [`${filterMappingPaymentStatus[v[0]].children}, ..`];
        }
        if (v.length === 1) {
          return [filterMappingPaymentStatus[v[0]].children];
        }
        return [];
      },
      update: (filterList: string[][],
              filterPos: number,
              index: number)  => {
        // update table filter state
        tableDispatch({
          type: 'UPDATE_FILTERS',
          payload: { ...state.filters, paymentStatus: [] },
        });

        // update table inner filter
        return customFilterChipRender(filterList, filterPos, index);
      },
    },
    filterOptions: {
      fullWidth: true,
      display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
        <CustomSelect
          className={classes.select}
          displayEmpty
          variant="filled"
          multiple
          // eslint-disable-next-line no-param-reassign,react/destructuring-assignment
          value={filterList[index]}
          menuItems={filterCodePaymentStatus}
         onChange={(e: SelectChangeEvent<unknown>) => {
            filterOnChange(filterList, onChange, index, column, e);
          }}
          label={t('admin->>PaymentReport.PaymentStatus')}
          labelId="inputSelectLabel-paymentStatus"
          id="inputSelect-paymentStatus"
          renderValue={(selected: unknown | string[]) =>
            Array.isArray(selected) && selected.length > 0
              ? // @ts-ignore
                filterCodePaymentStatus
                  .filter((paymentStatus: any) => selected.includes(paymentStatus.value))
                  .map((s: any) => s.children)
                  .join(', ')
              : t('admin->>PaymentReport.SelectPaymentStatus')
          }
        />
      ),
    },
  },
});
